import { useState, useEffect } from 'react'

import {
  getDrugs,
  getDrugComponents,
  getPharmacies,
  getPopularDrugs,
} from 'src/utils/rx/api'

const sortAndFormatDrugs = (drugs) => {
  const list = drugs.map((medication) => ({
    __type: 'medications',
    __parent: false,
    id: medication.sub_drug_name,
    value: `${medication.sub_drug_name} (${medication.typeName})`,
    items: medication.types.map((type) => ({
      __type: 'doses',
      __parent: medication.sub_drug_name,
      id: type.dose,
      value: type.dose,
      items: type.strength.map((strength) => ({
        __type: 'strength',
        __parent: type.dose,
        id: strength.Strength,
        value: strength.Strength,
        items: strength.Quantity.map((quantity) => ({
          __type: 'quantity',
          __parent: strength.Strength,
          id: quantity.Quantity,
          value: quantity.Quantity,
          displayValue: quantity.DisplayQuantity,
          ndcCode: quantity.NDCCode,
        })),
      })),
    })),
  }))

  const pullItems = (items) =>
    items.reduce(
      (prev, { items = false, ...curr }) => [
        ...prev,
        curr,
        ...(items ? pullItems(items) : []),
      ],
      [],
    )

  const sortedLists = pullItems(list).reduce(
    (prev, { __type, __parent = false, ...info }) => {
      if (__parent) {
        return {
          ...prev,
          [__type]: {
            ...(prev?.[__type] || {}),
            [__parent]: [...(prev?.[__type]?.[__parent] || []), info],
          },
        }
      } else {
        return { ...prev, [__type]: [...(prev?.[__type] || []), info] }
      }
    },
    {},
  )

  return sortedLists
}

export const useRxSearch = ({ defaultValues = {} } = {}) => {
  const { formState, setFormValue, onChange } = useRxSearchState({ defaultValues })
  const [selectedDrugName, setSelectedDrugName] = useState('')
  const [isLoadingComponents, setIsLoadingComponents] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const [isLoadingPharmacyList, setIsLoadingPharmacyList] = useState(false)
  const [pharmacyExtraInfoIsOpen, setPharmacyExtraInfoIsOpen] = useState({})

  const togglePharmacyOpen = (id) => {
    setPharmacyExtraInfoIsOpen((state) => {
      return {
        [id]: !state?.[id],
      }
    })
  }

  // popular drugs
  const { popularDrugs } = useRxPopularDrugs()

  // drug components
  const [drugComponents, setDrugComponents] = useState({})

  // pharmacies
  const [pharmacies, setPharmacies] = useState([])

  /**
   * Pharamcy List
   */

  const updatePharmacyList = async ({ state = {} } = {}) => {
    setErrorMessage(false)
    setIsLoadingPharmacyList(true)

    try {
      // state is not updated fast enough on first go, so we merge with passed in state
      const checkState = { ...(formState || {}), ...(state || {}) }

      const params = {
        zipcode: checkState?.zipcode,
        quantity: checkState?.quantity?.id,
        ndc: checkState?.quantity?.ndcCode,
      }

      if (!params.zipcode) {
        setErrorMessage('Enter your zipcode to find prescriptions near you.')

        return
      } else {
        const data = await getPharmacies(params)

        const formatPrice = (price, percent) => {
          const retailPrice = (price * 100) / (100 - percent)

          return retailPrice.toFixed(2)
        }

        const formatedData =
          data?.map?.((item) => {
            const percentSaved = item.PercentSaved?.replace(' %', '')

            return {
              ...item,
              percentSaved,
              yourPrice: item.Price.toFixed(2),
              retailPrice: formatPrice(item.Price, percentSaved),
            }
          }) || []

        setPharmacies(formatedData)
        setIsLoadingPharmacyList(false)
      }
    } catch (e) {
      setErrorMessage('There was an unkown error finding pharmacies near you.')
      setIsLoadingPharmacyList(false)
    } finally {
      setIsLoadingPharmacyList(false)
    }
  }

  const onUpdateList = async (e) => {
    e.preventDefault()

    await updatePharmacyList()
  }

  // when a drug is selected, get the components for that drug
  useEffect(() => {
    if (!selectedDrugName) {
      setIsLoadingComponents(false)
    } else {
      setIsLoadingComponents(true)

      const setupDrugComponents = async ({
        generic,
        brand,
        drugname,
        dose,
        strength,
        Quantity: quantity,
      }) => {
        const drugs = [
          ...generic.map((drug) => ({ ...drug, type: 'generic', typeName: 'Generic' })),
          ...brand.map((drug) => ({ ...drug, type: 'brand', typeName: 'Brand' })),
        ]

        const sortedLists = sortAndFormatDrugs(drugs)

        setDrugComponents(sortedLists)

        // set up default values in options
        const medicationDefaultValue = sortedLists.medications?.find(
          (item) => item.id === drugname,
        )
        setFormValue('medication', medicationDefaultValue)

        const doseDefaultValue = sortedLists.doses?.[medicationDefaultValue.id].find(
          (item) => item.id === dose,
        )
        setFormValue('dose', doseDefaultValue)

        const strengthDefaultValue = sortedLists.strength?.[doseDefaultValue.id].find(
          (item) => item.id === strength,
        )
        setFormValue('strength', strengthDefaultValue)

        const quantityDefaultValue = sortedLists.quantity?.[strengthDefaultValue.id].find(
          (item) => item.id === quantity,
        )
        setFormValue('quantity', quantityDefaultValue)

        await updatePharmacyList({ state: { quantity: quantityDefaultValue } })
      }

      const asyncGetDrugComponents = async () => {
        const data = await getDrugComponents(selectedDrugName)

        if (data) {
          setIsLoadingComponents(false)
          setupDrugComponents(data)
        }
      }

      asyncGetDrugComponents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDrugName])

  /**
   * Special UseEffects to help with managing drop down states
   *
   * When options are selected, we need to make sure to update the value of the
   * other dropdowns based on values, all the way down the line.
   */
  useEffect(() => {
    if (
      !drugComponents?.strength?.[formState.dose?.id]?.find?.(
        (item) => item.id === formState?.strength?.id,
      )
    ) {
      setFormValue('strength', drugComponents?.strength?.[formState.dose?.id]?.[0])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.dose])

  useEffect(() => {
    if (
      !drugComponents?.strength?.[formState.strength?.id]?.find?.(
        (item) => item.id === formState?.quantity?.id,
      )
    ) {
      setFormValue('quantity', drugComponents?.quantity?.[formState.strength?.id]?.[0])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.strength])

  /**
   * AutoComplete setup
   */
  const getSources = ({ query }) => {
    return [
      {
        sourceId: 'prescriptions',

        getItemInputValue({ item }) {
          return item.DrugName
        },

        async getItems() {
          if (!query) {
            return []
          }

          const data = await getDrugs(query)

          return data || []
        },

        onSelect({ item }) {
          setSelectedDrugName(item.DrugName)
        },

        templates: {
          item({ item }) {
            return <p>{item?.DrugName || 'unkown name'}</p>
          },

          noResults() {
            return 'No results found'
          },
        },
      },
    ]
  }

  // available options for all the dropdowns
  const options = {
    medications: drugComponents?.medications || [],
    doses: drugComponents?.doses?.[formState?.medication?.id] || [],
    strength: drugComponents?.strength?.[formState?.dose?.id] || [],
    quantity: drugComponents?.quantity?.[formState?.strength?.id] || [],
  }

  /**
   * Return all values/functions that are public
   */
  return {
    formState,
    setFormValue,
    onChange,
    onUpdateList,
    options,
    pharmacies,
    popularDrugs,
    isLoadingComponents,
    isLoadingPharmacyList,
    drugComponents,
    setDrugComponents,
    selectedDrugName,
    setSelectedDrugName,
    getSources,
    errorMessage,
    setErrorMessage,
    pharmacyExtraInfoIsOpen,
    togglePharmacyOpen,
  }
}

export const useRxSearchState = ({ defaultValues = {} } = {}) => {
  const [formState, setFormState] = useState({
    zipcode: '',
    medication: '',
    dose: '',
    strength: '',
    quantity: '',
    selectedDrugName: '',
    ...(defaultValues || {}),
  })

  const setFormValue = (key, value) => {
    setFormState((prevState) => {
      const newState = { ...prevState, [key]: value }

      return newState
    })
  }

  const onChange = (name) => (e) => {
    setFormValue(name, !e?.target ? e : e.target.value)
  }

  return { formState, setFormValue, onChange }
}

export const useRxPopularDrugs = () => {
  const [popularDrugs, setPopularDrugs] = useState([])

  useEffect(() => {
    const asyncGetPopularDrugs = async () => {
      const data = await getPopularDrugs()

      setPopularDrugs(data || [])
    }

    asyncGetPopularDrugs()
  }, [])

  return { popularDrugs }
}
